import type { Perfil, Permissao } from '@crm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, onSessaoPerdida, refresh, setAccessToken, onEmpresaBloqueada } from './api';

export interface Empresa {
  tenantId: string;
  tenantNome: string;
  tenantStatus: string;
  perfil: Perfil;
}

export interface Me {
  usuario: { id: string; nome: string; email: string; whatsapp?: string | null; trocarSenha?: boolean };
  empresa: {
    id: string; nome: string; status: string; timezone: string; plano?: { id: string; nome: string; maxUsuarios?: number | null };
    /** Fim do teste grátis (só em TRIAL) */
    trialAte?: string | null;
    bloqueio?: 'TESTE_EXPIRADO' | 'SUSPENSO' | null;
  };
  suporteWhatsapp?: string;
  perfil: Perfil;
  permissoes: Permissao[];
  empresas: Empresa[];
}

type LoginResultado =
  | { tipo: 'ok' }
  | { tipo: 'selecionar'; empresas: Empresa[]; preAuthToken: string };

interface AuthCtx {
  status: 'carregando' | 'anonimo' | 'autenticado';
  me: Me | null;
  pode: (p: Permissao) => boolean;
  entrar: (email: string, senha: string) => Promise<LoginResultado>;
  selecionarEmpresa: (preAuthToken: string, tenantId: string) => Promise<void>;
  cadastrar: (dados: Record<string, unknown>) => Promise<void>;
  trocarEmpresa: (tenantId: string) => Promise<void>;
  sair: () => Promise<void>;
  /** Relê /me (ex.: depois de salvar o WhatsApp em Minha conta) */
  recarregarMe: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);
const canal = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('crm-auth') : null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthCtx['status']>('carregando');
  const [me, setMe] = useState<Me | null>(null);

  const carregarMe = useCallback(async () => {
    const m = await api<Me>('/me');
    setMe(m);
    setStatus('autenticado');
  }, []);

  const aplicarToken = useCallback(
    async (token: string) => {
      setAccessToken(token);
      qc.clear();
      await carregarMe();
    },
    [qc, carregarMe],
  );

  useEffect(() => {
    onEmpresaBloqueada(() => void carregarMe().catch(() => {}));
    onSessaoPerdida(() => {
      setAccessToken(null);
      setMe(null);
      setStatus('anonimo');
    });
    refresh().then((ok) => (ok ? carregarMe().catch(() => setStatus('anonimo')) : setStatus('anonimo')));
    // Outra aba trocou de empresa ou saiu: recarrega para não misturar contextos
    const ouvir = () => window.location.reload();
    canal?.addEventListener('message', ouvir);
    return () => canal?.removeEventListener('message', ouvir);
  }, [carregarMe]);

  const valor = useMemo<AuthCtx>(
    () => ({
      status,
      me,
      pode: (p) => !!me?.permissoes.includes(p),
      entrar: async (email, senha) => {
        const r = await api<{ accessToken?: string; precisaSelecionarEmpresa?: boolean; empresas?: Empresa[]; preAuthToken?: string }>(
          '/auth/login',
          { body: { email, senha } },
        );
        if (r.precisaSelecionarEmpresa) return { tipo: 'selecionar', empresas: r.empresas!, preAuthToken: r.preAuthToken! };
        await aplicarToken(r.accessToken!);
        return { tipo: 'ok' };
      },
      selecionarEmpresa: async (preAuthToken, tenantId) => {
        const r = await api<{ accessToken: string }>('/auth/selecionar-empresa', { body: { preAuthToken, tenantId } });
        await aplicarToken(r.accessToken);
      },
      cadastrar: async (dados) => {
        const r = await api<{ accessToken: string }>('/auth/cadastro', { body: dados });
        await aplicarToken(r.accessToken);
      },
      trocarEmpresa: async (tenantId) => {
        const r = await api<{ accessToken: string }>('/auth/trocar-empresa', { body: { tenantId } });
        await aplicarToken(r.accessToken);
        canal?.postMessage('trocou');
      },
      sair: async () => {
        await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
        setAccessToken(null);
        qc.clear();
        setMe(null);
        setStatus('anonimo');
        canal?.postMessage('saiu');
      },
      recarregarMe: carregarMe,
    }),
    [status, me, aplicarToken, qc, carregarMe],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth fora do AuthProvider');
  return c;
}
